// Keep the conservative name scoring in parity with purchasing/utils/ocrItemMatch.js
// in the backend. AI text is matched only against the already supplier-scoped catalogue.
import { itemMatchesSupplier, itemSupplierMatches } from "./supplierIdentity";
import { ocrDescriptionForItemMatch, ocrItemAliasKey } from "./ocrItemSuggestions";
import { incompatible, nameParts, normalizeOcrItemText as normalized } from "./ocrItemCompatibility";
const MATCH_POLICY = "conservative_v1";
const MIN_NAME_SCORE = 0.82;
const MIN_NAME_MARGIN = 0.08;
const validId = (value) => /^\d+$/.test(String(value ?? "")) && Number(value) > 0;
const reference = (value) => normalized(value).replace(/\s/g, "");

function wordSimilarity(a, b) {
  if (a === b) return 1;
  if (Math.min(a.length, b.length) < 5 || Math.min(a.length, b.length) / Math.max(a.length, b.length) < 0.8) return 0;
  const rows = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 0; j <= b.length; j++) rows[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      rows[i][j] = Math.min(rows[i - 1][j] + 1, rows[i][j - 1] + 1, rows[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) rows[i][j] = Math.min(rows[i][j], rows[i - 2][j - 2] + 1);
    }
  }
  return 1 - rows[a.length][b.length] / Math.max(a.length, b.length);
}

function scoreName(description, name) {
  const input = normalized(description), candidate = normalized(ocrDescriptionForItemMatch(name));
  const a = nameParts(input), b = nameParts(candidate);
  if (!a.words.length || !b.words.length || incompatible(a, b)) return 0;
  if (input === candidate) return 1;
  if (input.replace(/\s/g, "") === candidate.replace(/\s/g, "")) return 0.99;
  if (a.words.length !== b.words.length) return 0;
  const remaining = [...b.words];
  let total = 0;
  const unmatched = a.words.filter((word) => {
    const index = remaining.indexOf(word);
    if (index < 0) return true;
    remaining.splice(index, 1); total += 1; return false;
  });
  for (const word of unmatched) {
    const scores = remaining.map((other) => wordSimilarity(word, other)), best = Math.max(...scores);
    if (best < 0.8) return 0;
    remaining.splice(scores.indexOf(best), 1); total += best;
  }
  return Math.min(total / a.words.length, 0.98);
}

function eligible(item, supplierId) {
  if (!item || !validId(item.id) || item.is_active === false || item.merged_into_item_id || item.deleted_at || item.deletedAt || item.is_deleted || item.deleted === true) return false;
  if (Array.isArray(item.item_suppliers)) return itemMatchesSupplier(item, supplierId);
  return item.supplier_id == null || itemSupplierMatches(item, supplierId);
}

/** Items must come from the endpoint scoped to this supplier and business unit; this cannot infer the BU. */
export function filterOcrAiSupplierItems(items, supplierId) {
  if (!validId(supplierId?.id ?? supplierId) || !Array.isArray(items)) return [];
  return [...new Map(items.filter((item) => eligible(item, supplierId)).map((item) => [String(item.id), item])).values()];
}

export function findOcrAiItemMatch(line, items, { supplierId } = {}) {
  if (!line) return null;
  const candidates = filterOcrAiSupplierItems(items, supplierId);
  const description = ocrDescriptionForItemMatch(line.description);
  const result = (item, source, score, runnerUpScore = 0) => ({ item, suggestion: {
    id: item.id, name: item.name, code: item.code, supplier_id: supplierId?.id ?? supplierId, source, score,
    runner_up_score: runnerUpScore, match_policy: MATCH_POLICY,
  } });
  for (const source of ["supplier_code", "internal_code"]) {
    const code = reference(source === "supplier_code" ? line.supplier_code : line.item_code || line.code || line.supplier_code);
    if (!code) continue;
    const matches = candidates.filter((item) => {
      const codes = source === "internal_code" ? [item.code] : [item.supplier_id == null || itemSupplierMatches(item, supplierId) ? item.supplier_code : null,
        ...(Array.isArray(item.item_suppliers) ? item.item_suppliers : []).filter((link) => itemSupplierMatches(link, supplierId)).map((link) => link.supplier_code)];
      return codes.some((value) => value && reference(value) === code);
    });
    if (matches.length) {
      if (matches.length !== 1 || incompatible(nameParts(description), nameParts(ocrDescriptionForItemMatch(matches[0].name)))) return null;
      const contradiction = candidates.some((item) => String(item.id) !== String(matches[0].id) && scoreName(description, item.name) >= 0.99);
      return contradiction ? null : result(matches[0], source, 1);
    }
  }
  const ranked = candidates.map((item) => ({ item, score: scoreName(description, item.name) }))
    .filter((match) => match.score > 0).sort((a, b) => b.score - a.score);
  const best = ranked[0], runnerUpScore = ranked[1]?.score || 0;
  if (!best || best.score < 0.99) {
    const key = ocrItemAliasKey(line.description);
    const learned = key ? candidates.filter((item) => Array.isArray(item.ocr_item_aliases) && item.ocr_item_aliases.includes(key)) : [];
    if (learned.length) return learned.length === 1 ? result(learned[0], "learning", 1) : null;
  }
  if (!best || best.score < MIN_NAME_SCORE || best.score - runnerUpScore < MIN_NAME_MARGIN) return null;
  return result(best.item, best.score >= 0.99 ? "exact_name" : "fuzzy", best.score, runnerUpScore);
}
